import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MoreHorizontal } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type MeditationMenuItem = {
    label: string;
    icon: LucideIcon;
    onSelect: () => void;
    disabled?: boolean;
};

type Props = {
    /** Whose actions these are, for the button's accessible name. */
    title: string;
    items: MeditationMenuItem[];
};

/**
 * The less frequent things to do with a meditation, behind one button.
 *
 * Opening it is the card itself; what is left — sharing it with patients,
 * publishing it, taking it back — is done now and then, and five buttons on
 * every card made a library of twenty a wall of controls.
 *
 * A menu button as the ARIA pattern has it: opening moves focus to the first
 * item, the arrows (and Home/End) move between them, Escape closes it and
 * hands focus back to the button, and tabbing away closes it too.
 */
export default function MeditationActionsMenu({ title, items }: Props) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const root = useRef<HTMLDivElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    const menu = useRef<HTMLUListElement>(null);
    const menuId = useId();

    const menuItems = () =>
        Array.from(
            menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ??
                []
        );

    useEffect(() => {
        if (!open) return;
        menuItems()[0]?.focus();
        const outside = (event: MouseEvent) => {
            if (!root.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", outside);
        return () => document.removeEventListener("mousedown", outside);
    }, [open]);

    const close = (returnFocus: boolean) => {
        setOpen(false);
        if (returnFocus) trigger.current?.focus();
    };

    const onMenuKeyDown = (event: React.KeyboardEvent) => {
        const entries = menuItems();
        const at = entries.indexOf(document.activeElement as HTMLButtonElement);
        const focus = (index: number) => entries[(index + entries.length) % entries.length]?.focus();
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                focus(at + 1);
                break;
            case "ArrowUp":
                event.preventDefault();
                focus(at - 1);
                break;
            case "Home":
                event.preventDefault();
                focus(0);
                break;
            case "End":
                event.preventDefault();
                focus(entries.length - 1);
                break;
            case "Escape":
                event.preventDefault();
                close(true);
                break;
            case "Tab":
                close(false);
                break;
        }
    };

    if (items.length === 0) return null;

    return (
        <div ref={root} className="relative">
            <button
                ref={trigger}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label={t("meditation.actions.more", { title })}
                title={t("meditation.actions.moreShort")}
                onClick={() => setOpen((value) => !value)}
                onKeyDown={(event) => {
                    // The arrows open it as well, as a menu button's should.
                    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
                className="flex size-9 items-center justify-center rounded-lg text-content-secondary hover:bg-surface-sunken"
            >
                <MoreHorizontal size={18} aria-hidden="true" />
            </button>
            {open && (
                <ul
                    ref={menu}
                    id={menuId}
                    role="menu"
                    aria-label={t("meditation.actions.more", { title })}
                    onKeyDown={onMenuKeyDown}
                    className="absolute right-0 z-30 mt-1 min-w-52 rounded-xl border border-border-subtle bg-surface-raised p-1 shadow-theme-lg"
                >
                    {items.map(({ label, icon: Icon, onSelect, disabled }) => (
                        <li key={label} role="none">
                            <button
                                type="button"
                                role="menuitem"
                                tabIndex={-1}
                                disabled={disabled}
                                onClick={() => {
                                    close(true);
                                    onSelect();
                                }}
                                className="flex min-h-11 w-full items-center gap-2 rounded-lg px-3 text-left text-sm text-content-primary hover:bg-surface-sunken disabled:opacity-50"
                            >
                                <Icon size={15} aria-hidden="true" className="text-content-secondary" />
                                {label}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
